import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Download, Zap, TrendingUp, ShieldCheck, Check, X } from 'lucide-react';
import { ShowcaseDisplay } from './ShowcaseDisplay';

// Define Stripe pricing table for JSX
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'stripe-pricing-table': any;
    }
  }
}

export const BusinessLanding = () => {
  console.log("BusinessLanding rendering");
  return (
    <div className="min-h-screen bg-neutral-950 text-white font-sans overflow-x-hidden transition-colors duration-300">
        {/* Header - kept without toggle */}
        <header className="fixed top-0 left-0 right-0 p-[2vh] flex justify-between items-center z-50">
          <div className="font-bold tracking-tighter text-[2vh]">AURA</div>
        </header>

        {/* Hero */}
        <section className="pt-[15vh] pb-[10vh] px-[5vw] text-center">
          <div className="max-w-5xl mx-auto space-y-[2vh]">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-[1.2vh] uppercase tracking-[0.3em] text-neutral-400">
              Aura Business Ecosystem
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-[clamp(3rem,8vh,8rem)] font-light tracking-tighter text-white leading-[0.9]">
              Neuro-Ambientación para<br/> <span className="text-yellow-500">el comercio de proximidad.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-[clamp(1rem,2.5vh,2rem)] text-neutral-300 max-w-2xl mx-auto">
              El silencio y la cartelería estática ya no venden. Transformamos tu local en un vendedor silencioso y constante que aumenta el tiempo de permanencia mediante marketing sensorial.
            </motion.p>
          </div>
        </section>

        {/* Science of Retail */}
        <section className="py-20 px-6 max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
             <h2 className="text-4xl font-light tracking-tighter">La Ciencia tras <span className="font-bold">Aura Business</span></h2>
             <div className="space-y-6 text-neutral-200">
                <p><strong>De Papel a Digital:</strong> La publicidad estática es invisible. Aura sustituye el ruido visual por movimiento dinámico que captura la mirada y comunica ofertas en tiempo real.</p>
                <p><strong>Marketing Sensorial:</strong> No compras pantallas, compras tiempo. La música adaptativa y el visual storytelling bien sincronizados aumentan el ticket medio y la permanencia en el local.</p>
                <p><strong>Optimización de Costes:</strong> Olvida los costes de imprenta constante, gestión de vinilos y publicidad externa. Todo está centralizado, autogestionable y bajo tu control.</p>
             </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="py-20 px-6 border-t border-neutral-200 dark:border-neutral-800">
          <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-12">
            {[
              { icon: ShieldCheck, title: "Paz Legal (SGAE)", desc: "Olvídate de las cuotas abusivas. Aura es 100% libre de derechos de autor." },
              { icon: Zap, title: "Neuro-Ambientación", desc: "Música que se adapta al biorritmo y necesidad de venta de cada hora del día." },
              { icon: TrendingUp, title: "Aura 360 Ecosistema", desc: "Gestión unificada de cartelería digital sincronizada con tu hilo musical." },
            ].map((item, i) => (
              <div key={i} className="space-y-4">
                <item.icon className="text-yellow-600" size={32} />
                <h3 className="text-xl font-bold">{item.title}</h3>
                <p className="text-neutral-200">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing Table (Stripe) */}
        <section className="py-20 px-6 bg-neutral-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto">
            {React.createElement('stripe-pricing-table', {
              'pricing-table-id': "prctbl_1TWFiaCS83xMzfkZPhbCsyDy",
              'publishable-key': "pk_live_51TGuufCS83xMzfkZU9tlAFxAS9ZGsOLNJppMDCatJtkFBCKCZW2PQYgetKCL3XB4rJ11syj3VZTNeWRZLhqTDbRa00pwEiWaFC"
            })}
          </div>
        </section>

        {/* AuraCredits Explanation */}
      <section className="py-20 px-6 bg-neutral-950">
        <div className="max-w-4xl mx-auto border border-yellow-500/30 rounded-3xl p-12 bg-neutral-900">
          <h2 className="text-4xl mb-6 text-white">Autofinancia tu transformación con AuraCredits</h2>
          <div className="grid md:grid-cols-2 gap-8">
             <div className="space-y-4">
               <p className="text-neutral-300">
                 No solo gestionas tu cartelería; te conviertes en un nodo activo de nuestra red comercial. 
                 Por cada slide colaborativo (espacio publicitario cedido a comercios vecinos) que se emite en tu pantalla, 
                 acumulas <strong>AuraCredits</strong>.
               </p>
               <p className="text-neutral-300">
                 Es un modelo de economía colaborativa donde tu infraestructura genera ingresos pasivos. 
                 Al alcanzar 100 AuraCredits, tu suscripción mensual es totalmente gratuita.
               </p>
             </div>
             <div className="p-6 bg-neutral-800 rounded-xl border border-yellow-500/20 shadow-lg">
                <div className="text-yellow-500 font-bold tracking-widest uppercase text-xs mb-3">Simulación de ahorro</div>
                <p className="text-sm text-neutral-300">
                  Gestionando tus 5 slots colaborativos, no solo ayudas a dinamizar el comercio de proximidad, 
                  sino que cubres gran parte o la totalidad de tu cuota mensual de AuraConnect.
                </p>
                <div className="mt-4 text-3xl font-bold text-white">100 A.C. = 1 Mes Gratis</div>
             </div>
          </div>
        </div>
      </section>

      {/* Collective Spaces Solution */}
      <section className="py-20 px-6 bg-neutral-900/50">
        <div className="max-w-4xl mx-auto space-y-6 text-center">
          <h2 className="text-4xl">Mercados de Abastos y Afines</h2>
          <p className="text-xl text-neutral-200">
            Estudio personalizado y consultoría integral de marca.<br/>
            Cuando los comerciantes compiten entre sí por la atención (ruido vs. música, carteles pegados con celo vs. pantallas), el cliente se estresa y compra menos. <br/>
            Nuestro valor es <strong>orden, profesionalización y una experiencia de compra unificada</strong>.
          </p>

          <div className="pt-8 grid md:grid-cols-2 gap-8 text-left">
            <div className="p-6 border border-neutral-800 rounded-xl bg-neutral-950">
              <h4 className="text-lg font-bold mb-2">Precio por establecimiento</h4>
              <p className="text-neutral-200 text-sm">desde 15€ por puesto (Opción Aura Pro) para una gestión individualizada pero integrada en la red global del recinto.</p>
            </div>
            <div className="p-6 border border-neutral-800 rounded-xl bg-neutral-950">
              <h4 className="text-lg font-bold mb-2">Gestión Global Gerencia</h4>
              <p className="text-neutral-200 text-sm">Contratación centralizada para todo el recinto con condiciones especiales por volumen, garantizando uniformidad visual y sonora total.</p>
            </div>
          </div>
        </div>
      </section>

        {/* Real-time Preview */}
        <section className="py-20 px-6 max-w-7xl mx-auto">
          <h2 className="text-4xl font-light tracking-tighter mb-12 text-center">Vista Previa</h2>
          <ShowcaseDisplay />
        </section>

    </div>
  );
};
